<template>
  <modal-wrapper
    ref="modalRef"
    :busy="isSubmitting"
    :close-on-ok-click="false"
    :content-overflow-y="'visible'"
    :header="t('workspaceGroupsManagement.addGroup')"
    :ok-button-label="t('generic.create')"
    @ok-button-click="handleCreateGroup"
    @cancel-button-click="resetForm"
  >
    <div class="modal-content">
      <label for="workspace-group-name">
        {{ t('workspaceGroupsManagement.groupName') }}
      </label>
      <PVInputText
        id="workspace-group-name"
        v-model="groupName"
        :placeholder="t('workspaceGroupsManagement.groupNamePlaceholder')"
      />

      <message-wrapper v-if="isNameEmpty" simple-error>
        {{ t('workspaceGroupsManagement.errors.groupNameRequired') }}
      </message-wrapper>
      <message-wrapper v-else-if="isNameNotUnique" simple-error>
        {{ t('workspaceGroupsManagement.errors.groupNameNotUnique') }}
      </message-wrapper>
    </div>
  </modal-wrapper>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from 'axios';
  import { useModalWrapper } from '../ModalWrapper';
  import { useToast } from '@/composables/useToast';
  import { endpointsList } from '@/helpers';
  import { IValidateWorkspaceGroupNameDto } from 'types';
  import { useWorkspaceGroups } from '@/composables/api/workspaces/useWorkspaceGroups';
  import MessageWrapper from '@/components/MessageWrapper/MessageWrapper.vue';
  import ModalWrapper from '../ModalWrapper/ModalWrapper.vue';
  import PVInputText from 'primevue/inputtext';

  const props = defineProps<{
    workspaceId: string;
  }>();

  const emit = defineEmits<{
    (e: 'group-created'): void;
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const { createWorkspaceGroup } = useWorkspaceGroups(props.workspaceId);

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const groupName = ref('');
  const isSubmitting = ref(false);
  const isSubmitted = ref(false);
  const isNameNotUnique = ref(false);

  const isNameEmpty = computed(
    () => isSubmitted.value && !groupName.value.trim(),
  );

  async function handleCreateGroup() {
    isSubmitted.value = true;
    isNameNotUnique.value = false;

    const trimmedName = groupName.value.trim();
    if (!trimmedName) return;

    isSubmitting.value = true;
    try {
      const payload: IValidateWorkspaceGroupNameDto = { name: trimmedName };
      const response = await axios.post<boolean>(
        endpointsList.workspaces.validateWorkspaceGroupName(props.workspaceId),
        payload,
      );
      if (!response.data) {
        isNameNotUnique.value = true;
        return;
      }

      const isCreated = await createWorkspaceGroup(trimmedName);
      if (isCreated) {
        emit('group-created');
        resetForm();
        hide();
      }
    } catch {
      createErrorToast(
        t('workspaceGroupsManagement.errors.errorValidatingGroupName'),
      );
    } finally {
      isSubmitting.value = false;
    }
  }

  function resetForm() {
    groupName.value = '';
    isSubmitted.value = false;
    isNameNotUnique.value = false;
  }
</script>

<style lang="scss" scoped>
  .modal-content {
    display: flex;
    flex-direction: column;
    row-gap: 0.75rem;

    label {
      font-weight: 600;
    }
  }
</style>
